import {
  CheckIcon,
  ChevronRightIcon,
  FieldValidation,
  Input,
  TextField,
  XmarkIcon,
  getErrorMessage,
} from "@wexinc/wex-ui";
import classNames from "classnames";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import * as yup from "yup";

interface Props {
  username: string;
}

const fieldName = "password";

const PasswordConfirmPassword: React.FC<Props> = (props) => {
  const { register, formState, watch, trigger } = useFormContext();
  const { ref, onChange, ...registerRest } = register(fieldName);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const requiredErrorMessage = getErrorMessage(formState.errors, fieldName);
  const passwordMatchValidationError = get(formState.errors, "password.types.oneOf");
  const [currentValue, setCurrentValue] = useState<string>();

  useEffect(() => {
    if (currentValue) {
      const currentValidationErrors = validatePassword(currentValue, props.username);
      setValidationErrors(currentValidationErrors);
    }
  }, [currentValue]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (formState.isSubmitted && value["password"]?.length > 0 && value["confirmPassword"]?.length > 0) {
        trigger(["password", "confirmPassword"]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, formState]);

  return (
    <>
      <Input
        showAsCritical={
          (validationErrors.length > 0 && formState.isSubmitted) ||
          (!!requiredErrorMessage && requiredErrorMessage?.toString().length > 0) ||
          (passwordMatchValidationError && formState.isSubmitted)
        }
        containerClassName="mb-6"
        label="Password"
        type="password"
        refCallBack={ref}
        onChange={(e) => {
          setCurrentValue(e.target.value);
          onChange(e);
        }}
        {...registerRest}
        otherContent={
          <FieldValidation
            testId={`${fieldName}-field-validation`}
            error={
              requiredErrorMessage || (formState.isSubmitted && passwordMatchValidationError)
                ? passwordMatchValidationError?.toString()
                : undefined
            }
            className="absolute -bottom-4"
          />
        }
      />

      <ul className="my-4">
        {getValidationStatuses(validationErrors, !!currentValue && formState.isSubmitted).map((i) => i)}
      </ul>

      <TextField
        name="confirmPassword"
        label="Confirm password"
        type="password"
      />
    </>
  );
};

interface PasswordConfirmPasswordSchema {
  password: string;
  confirmPassword: string;
}

export function getSchema(username: string) {
  return yup.object<PasswordConfirmPasswordSchema>({
    password: yup
      .string()
      .trim()
      .required("Password is required.")
      .test("all-validations", "", (value) => {
        if (value) {
          const result = validatePassword(value!, username);
          return result.length === 0;
        }
        return true;
      }),
    confirmPassword: yup
      .string()
      .trim()
      .oneOf([yup.ref("password")], "Confirm password does not match")
      .required("Confirm Password is required."),
  });
}

const validations = {
  lowercase: {
    description: "at least 1 lowercase letter",
    validator: (val: string) => /(?=.*[a-z])/.test(val),
  },
  minLength: {
    description: "8 or more characters",
    validator: (val: string) => val.length > 7,
  },
  number: {
    description: "at least 1 number",
    validator: (val: string) => /(?=.*\d)/.test(val),
  },
  uppercase: {
    description: "at least 1 uppercase letter",
    validator: (val: string) => /(?=.*[A-Z])/.test(val),
  },
  partOfUsername: {
    description: "does not contain part of username",
    validator: (val: string, userName: string) => {
      val = val.toLowerCase();
      // See: https://support.okta.com/help/s/article/Password-rejected-even-though-it-meets-all-requirements?language=en_US
      const usernamePartsWithoutTopLevelDomain = userName.toLowerCase().replace("@", ".").split(".").slice(0, -1);
      return usernamePartsWithoutTopLevelDomain.some((i) => val.indexOf(i) !== -1) === false;
    },
  },
  specialChars: {
    description: "at least 1 special character",
    validator: (val: string) => /[*.!@#$%^&(){}:;<>,?]+/.test(val),
  },
  threeRepeat: {
    description: "does not repeat the same character 3 times in succession",
    validator: (val: string) => !/(.)\1{2,}/.test(val.toLowerCase()),
  },
  similarToPassword: {
    description: "does not contain the phrase 'password'",
    validator: (val: string) => !/p[a@4]?[s5$]{1,2}w[o0]?[rR]d/.test(val.toLowerCase()),
  },
};

function validatePassword(password: string, username: string) {
  const result: string[] = [];
  for (const [key, value] of Object.entries(validations)) {
    if (value.validator(password, username) === false) {
      result.push(key);
    }
  }
  return result;
}

function getValidationStatuses(validationErrors: string[], doIndicateValidity: boolean) {
  const result: JSX.Element[] = [];

  for (const [key, value] of Object.entries(validations)) {
    const hasError = validationErrors.includes(key);

    result.push(
      <li
        key={key}
        className="flex items-center py-2 h-8 relative"
      >
        {doIndicateValidity && hasError && <XmarkIcon className="fill-current text-utility-critical-60" />}
        {doIndicateValidity && !hasError && (
          <CheckIcon className="fill-current text-utility-success-60 scale-[.70] -ml-1" />
        )}
        {!doIndicateValidity && <ChevronRightIcon className="fill-current text-neutral-dark-30 scale-[1.3]" />}
        <label
          className={classNames(
            "text-sm absolute left-6 ",
            { "text-utility-critical-60": doIndicateValidity && hasError },
            { "text-utility-success-60": doIndicateValidity && !hasError },
          )}
        >
          {value.description}
        </label>
      </li>,
    );
  }

  return result;
}

export default PasswordConfirmPassword;
